import {
  BusinessFileType,
  IBusinessFile,
} from "../../../../interfaces/businessFile.interface";
import {
  ADYEN_DOCUMENT_REASON,
  AdyenDocumentAcknowledgement,
  AdyenDocumentStateRow,
  AdyenDocumentPageType,
  AdyenDocumentReason,
  normalizeAdyenDocumentReasonCode,
} from "../../../../interfaces/adyenDocumentState.interface";

/**
 * Which row of a submission group carries the control. Lower wins, so the
 * front side owns the control and the back side never renders its own.
 */
const SUBMISSION_PRIMARY_PRIORITY: Record<string, number> = {
  [BusinessFileType.COMPANY_INFO_FILE]: 0,
  [BusinessFileType.PERSON_IDENTITY_FRONT_FILE]: 1,
  [BusinessFileType.IDV_DOCUMENT_FILE]: 2,
  [BusinessFileType.PERSON_IDENTITY_BACK_FILE]: 3,
};

const primaryPriority = (row: AdyenDocumentStateRow): number =>
  SUBMISSION_PRIMARY_PRIORITY[row.documentType] ?? 99;

export type AdyenDocumentFetchState = {
  isLoadingAdyenDocuments: boolean;
  adyenDocumentsBusinessId: string | null;
  /** Latest in-flight/settled fetch; stale responses for the same business drop. */
  adyenDocumentsRequestId: string | null;
};

/**
 * Keeps one business to one request even though the Files tab and every person
 * row mount the hook — `pending` lands synchronously, so a same-tick second
 * dispatch sees the in-flight fetch. Only the same business blocks.
 */
export function shouldFetchAdyenDocumentState(
  state: AdyenDocumentFetchState,
  params: { businessId: string; force?: boolean }
): boolean {
  if (
    state.isLoadingAdyenDocuments &&
    state.adyenDocumentsBusinessId === params.businessId
  ) {
    return false;
  }
  if (params.force) return true;
  return state.adyenDocumentsBusinessId !== params.businessId;
}

/**
 * Whether a settled response may write: it must still be the latest request for
 * this business. Lives here, not in the slice, because importing the slice from
 * a test constructs the store and throws.
 */
export function isAdyenDocumentResponseCurrent(
  state: Pick<
    AdyenDocumentFetchState,
    "adyenDocumentsBusinessId" | "adyenDocumentsRequestId"
  >,
  respondedBusinessId: string,
  requestId: string
): boolean {
  return (
    state.adyenDocumentsBusinessId === respondedBusinessId &&
    state.adyenDocumentsRequestId === requestId
  );
}

export type AdyenSubmissionGroup = {
  submissionKey: string;
  rows: AdyenDocumentStateRow[];
};

/**
 * The key rows are grouped by. `submissionKey` is optional on the wire, and
 * grouping several rows under one absent key would put unrelated documents
 * behind a single control, so a row without one stands alone.
 */
function submissionGroupKey(row: AdyenDocumentStateRow, index: number): string {
  return row.submissionKey ?? `ungrouped:${row.documentType}:${index}`;
}

/** Rows sharing a submissionKey are one submission, so they get one control. */
export function groupRowsBySubmission(
  rows: AdyenDocumentStateRow[]
): AdyenSubmissionGroup[] {
  const groups = new Map<string, AdyenDocumentStateRow[]>();
  rows.forEach((row, index) => {
    const key = submissionGroupKey(row, index);
    const existing = groups.get(key);
    if (existing) existing.push(row);
    else groups.set(key, [row]);
  });

  return Array.from(groups.entries()).map(([submissionKey, groupRows]) => ({
    submissionKey,
    rows: groupRows,
  }));
}

/** A row the backend refuses cannot carry a control, on either surface. */
function canOwnControl(row: AdyenDocumentStateRow): boolean {
  return row.eligibility !== "not_submittable";
}

const byPrimaryPriority = (
  best: AdyenDocumentStateRow | undefined,
  row: AdyenDocumentStateRow
): AdyenDocumentStateRow =>
  !best || primaryPriority(row) < primaryPriority(best) ? row : best;

/**
 * Which row of one submission owns the control. Both surfaces resolve it through
 * here, so the Files tab and the person editor cannot disagree about whether a
 * submission can be sent. Undefined when every row in the group is refused.
 */
export function controlOwner(
  rows: AdyenDocumentStateRow[]
): AdyenDocumentStateRow | undefined {
  return rows
    .filter(canOwnControl)
    .reduce<AdyenDocumentStateRow | undefined>(byPrimaryPriority, undefined);
}

/**
 * The row that speaks for a submission where it is rendered once. The control
 * owner when there is one, so the details shown are the ones the button acts
 * on; otherwise the leading row, so a wholly refused submission still reports
 * itself rather than vanishing.
 */
export function submissionDisplayRow(
  rows: AdyenDocumentStateRow[]
): AdyenDocumentStateRow | undefined {
  return (
    controlOwner(rows) ??
    rows.reduce<AdyenDocumentStateRow | undefined>(byPrimaryPriority, undefined)
  );
}

export type AdyenSubmissionSideReason = {
  documentType: BusinessFileType;
  reason: string;
};

/**
 * Reasons held by the rest of a submission, deduplicated against the displayed
 * row. Collapsing a group to one panel would otherwise drop the explanation for
 * a side that was refused while another side still carries the control.
 */
export function otherSubmissionReasons(
  rows: AdyenDocumentStateRow[],
  displayed?: AdyenDocumentStateRow
): AdyenSubmissionSideReason[] {
  const seen = new Set(displayed?.reason ? [displayed.reason] : []);

  return rows.reduce<AdyenSubmissionSideReason[]>((reasons, row) => {
    if (
      row === displayed ||
      !row.reason ||
      seen.has(row.reason) ||
      isDownloadedFromAdyenOnly(row)
    )
      return reasons;
    seen.add(row.reason);
    return [...reasons, { documentType: row.documentType, reason: row.reason }];
  }, []);
}

/**
 * Adyen holds the merchant's own document and the only bytes we have are its
 * copy. Not a fault: there is simply nothing of ours to send.
 */
export function isDownloadedFromAdyenOnly(row: AdyenDocumentStateRow): boolean {
  return (
    row.adyenDocument?.origin === "downloaded" &&
    normalizeAdyenDocumentReasonCode(row.reason) ===
      ADYEN_DOCUMENT_REASON.adyenSourcedBytes
  );
}

export function rowsForPerson(
  rows: AdyenDocumentStateRow[],
  personId: string
): AdyenDocumentStateRow[] {
  return rows.filter((row) => row.personId === personId);
}

/** What the control will actually send, so the operator is never surprised. */
export function describeSidesToBeSubmitted(
  sides?: AdyenDocumentPageType[]
): string {
  const list = sides ?? [];
  const hasFront = list.includes("front");
  const hasBack = list.includes("back");

  if (hasFront && hasBack) return "front and back";
  if (hasFront) return "front only, no back on file";
  if (hasBack) return "back only";
  return "";
}

/**
 * The acknowledgement the read already proves is needed, confirmed before the
 * first call. The merchant-document gate is only evaluated live, so it never
 * appears here; an unrecognised reason waits for that same 409 too.
 */
export function requiredAcknowledgementFromRow(
  row: AdyenDocumentStateRow
): AdyenDocumentAcknowledgement | null {
  if (row.eligibility !== "submittable_with_acknowledgement") return null;

  const reason = normalizeAdyenDocumentReasonCode(row.reason);
  const provesApproval =
    reason === ADYEN_DOCUMENT_REASON.ownerApproved ||
    reason === ADYEN_DOCUMENT_REASON.ownerScopeNotPersisted;

  return provesApproval ? "approvedOwner" : null;
}

/**
 * The gate a live refusal asks for. `owner_scope_not_persisted` is a refusal in
 * its own right, cleared by the same acknowledgement the approved-owner gate takes.
 */
export function acknowledgementForReason(
  reason: AdyenDocumentReason
): AdyenDocumentAcknowledgement | null {
  const code = normalizeAdyenDocumentReasonCode(reason.code);
  if (
    code === ADYEN_DOCUMENT_REASON.ownerApproved ||
    code === ADYEN_DOCUMENT_REASON.ownerAlreadyApproved ||
    code === ADYEN_DOCUMENT_REASON.ownerScopeNotPersisted
  ) {
    return "approvedOwner";
  }
  if (code === ADYEN_DOCUMENT_REASON.wouldDeactivateMerchantDocument) {
    return "merchantDocument";
  }
  return null;
}

/** No `code` in the body means the rejection never reached our handler. */
const ADYEN_DOCUMENT_UNKNOWN_REASON = "unknown_error";

type AdyenDocumentErrorBody = { code?: string | number; message?: string };

/**
 * The backend body from either rejection shape this app produces: on an axios
 * error's `response.data`, or rejected on its own by the shared interceptor. A
 * response-less axios error yields nothing — `ERR_NETWORK` is not a backend code.
 */
function readAdyenDocumentErrorBody(
  error: unknown
): AdyenDocumentErrorBody | undefined {
  const fromResponse = (error as { response?: { data?: unknown } })?.response
    ?.data;
  if (fromResponse) return fromResponse as AdyenDocumentErrorBody;
  if ((error as { isAxiosError?: boolean })?.isAxiosError) return undefined;
  return error as AdyenDocumentErrorBody | undefined;
}

/** Reads the backend's `{ message, code }` so a refusal can name itself. */
export function extractAdyenDocumentReason(
  error: unknown
): AdyenDocumentReason {
  const body = readAdyenDocumentErrorBody(error);

  if (body?.code !== undefined && body?.code !== null) {
    return { code: String(body.code), message: body.message };
  }

  // A 401 carries no code, and these routes skip the refresh-and-retry interceptor.
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 401) {
    return {
      code: ADYEN_DOCUMENT_REASON.sessionExpired,
      message: body?.message,
    };
  }

  return {
    code: ADYEN_DOCUMENT_UNKNOWN_REASON,
    message:
      body?.message ||
      (error as Error)?.message ||
      "The submission failed for an unknown reason.",
  };
}

/**
 * True when the failure never reached our handler — a route that is not
 * deployed, a gateway refusal, an offline browser. Nothing to tell an operator.
 */
export function isAdyenDocumentStateUnavailable(
  reason: AdyenDocumentReason
): boolean {
  return reason.code === ADYEN_DOCUMENT_UNKNOWN_REASON;
}

export type AdyenFileAnnotationStatus =
  /** This file is the one resubmission would send. */
  | "live"
  /** A newer file of the same type won; this one cannot be sent. */
  | "superseded"
  /** Rows exist for the type but the payload cannot tie them to this file. */
  | "unattributable"
  /** The read endpoint reports nothing for this document type. */
  | "noState";

export type AdyenFileAnnotation = {
  status: AdyenFileAnnotationStatus;
  row?: AdyenDocumentStateRow;
  showControl: boolean;
};

const fileGroupKey = (type: BusinessFileType, personId?: string): string =>
  `${type}::${personId ?? ""}`;

const rowHasFileCoordinates = (row: AdyenDocumentStateRow): boolean =>
  Boolean(row.file?.bucketName && row.file?.fileName);

/**
 * Ties each rendered file to the state row that describes it, joining on S3
 * coordinates. Safe when this UI ships before the paired backend: missing
 * coordinates never invent a "superseded" match, and an undeployed state route
 * leaves every file as `noState` (Files tab unchanged).
 */
export function buildAdyenFileAnnotations(params: {
  rows: AdyenDocumentStateRow[];
  files: IBusinessFile[];
}): AdyenFileAnnotation[] {
  const { rows, files } = params;

  const filesPerGroup = new Map<string, number>();
  for (const file of files) {
    const key = fileGroupKey(file.type, file.personId);
    filesPerGroup.set(key, (filesPerGroup.get(key) ?? 0) + 1);
  }

  const joined: JoinedFile[] = files.map((file) => {
    const candidates = rows.filter(
      (row) =>
        row.documentType === file.type &&
        (file.personId ? row.personId === file.personId : true)
    );

    if (candidates.length === 0) return { status: "noState" };

    if (file.sourceBucketName && file.sourceFileName) {
      const matched = candidates.find(
        (row) =>
          row.file?.bucketName === file.sourceBucketName &&
          row.file?.fileName === file.sourceFileName
      );
      if (matched) return { status: "live", row: matched };
      // Superseded only when a candidate actually carries coordinates to lose to.
      // Rows without them (older payload / partial deploy) fall through — claiming
      // superseded would paint the wrong file's Adyen details beside this one.
      if (candidates.some(rowHasFileCoordinates)) {
        return { status: "superseded", row: candidates[0] };
      }
    }

    // No joinable coordinates on either side: only an unambiguous single file.
    const siblingCount =
      filesPerGroup.get(fileGroupKey(file.type, file.personId)) ?? 0;
    if (candidates.length === 1 && siblingCount === 1) {
      return { status: "live", row: candidates[0] };
    }
    return { status: "unattributable" };
  });

  return applyControlOwnership(joined);
}

type JoinedFile = Omit<AdyenFileAnnotation, "showControl">;

/**
 * Grants the control to one live annotation per submissionKey, so front and
 * back never render a control each. A row with no key owns its own control:
 * sharing one across unkeyed rows would hide a document behind another's button.
 */
function applyControlOwnership(joined: JoinedFile[]): AdyenFileAnnotation[] {
  const ownerPerKey = new Map<
    string,
    { index: number; row: AdyenDocumentStateRow }
  >();

  joined.forEach(({ status, row }, index) => {
    if (status !== "live" || !row) return;
    if (!canOwnControl(row)) return;

    const key = row.submissionKey ?? `ungrouped:${index}`;
    const owner = ownerPerKey.get(key);
    if (!owner || primaryPriority(row) < primaryPriority(owner.row)) {
      ownerPerKey.set(key, { index, row });
    }
  });

  const owners = new Set(
    Array.from(ownerPerKey.values(), (owner) => owner.index)
  );
  return joined.map((annotation, index) => ({
    ...annotation,
    showControl: owners.has(index),
  }));
}
